import * as THREE from "three";
import { BalanceData, Cell, Enemy, EnemyArchetype } from "@highmarch/domain";
import type { VFX } from "../../../../art/vfx.js";
import { lib, hqEnemyName, hqNode } from "./views/common";
import { buildEnemyView, resetEnemyView } from "./views/enemyBuild";
import { EMERGE_SECONDS, EnemyView } from "./views/enemyView";
import { EnemyWash, hitFeel, stepUnit } from "./views/enemyFeel";
import { dieStep, startDeath } from "./views/enemyDeath";
import { enemyLook } from "./views/enemyLook";
import { KillFx } from "./views/killFx";
import type { BossStage } from "./views/bossStage";
import { softDisc } from "./views/critterFlock";
import { RigidRig } from "./rigidRig";
import { StrikeLight } from "./strikeLight";
import { StrikeBeats } from "./fxHooks";
import { SkinFx } from "./skins/kit/fx";
import { reducedVfx } from "../ui/settings";
import { HubCampLayout } from "./hubCampLayout";
import { BoltPlan, HUB_RAID, RaidPath, RaidPlan, RaiderKind, mistFade, nextRaidIn, planRaid } from "./hubRaidPlan";

/** One pooled raider on the road: its enemy view, its plan in the current raid, how far it has run. */
interface Raider { readonly v: EnemyView; plan: RaidPlan["raiders"][number] | null; s: number; dead: boolean; dying: boolean }

/**
 * The hub's raids (owner 2026-10-24): every 8–15 s a small party of the Ashen Horde's core raiders comes down the road
 * out of the mist at the top of the screen (they set off out of sight and dissolve in as they cross into it) and
 * charges the camp gate, and the fortress's conductor (`condgem0`, every tier and every skin) strikes them down with
 * the level's own bolts — zaps (`baseZap`) for most, the full strike (`baseStrike`) only for the raid's opening bolt
 * and a brute's death (hubRaidPlan.ts boltIsStrike) — in the worn fortress's strike colours, before they reach it. The plan (who, when, which bolt kills
 * whom) is hubRaidPlan.ts; this draws it with the level's own enemy views: the same models and clips, the
 * distance-driven walk with planted feet (enemyFeel.ts stepUnit), the same deaths (enemyDeath.ts, the worn kill
 * effect) and the strike light. A stand-in hub needs no sim: each view is fed its path position as the sim would be.
 *
 * Cost: at most `HUB_RAID.cap` live raiders (fewer on the low tiers), pooled for the hub's life, each on its LOD model
 * with a soft blob instead of a shadow-map cast; nothing is allocated per frame. Silent (the hub plays no scene
 * sounds), and none at all under Reduced effects or before the sprite VFX has loaded. The hub's tick stops under a
 * full-screen sheet and in the background, and this with it. Pure presentation: no game state is read or written.
 */
export class HubRaid {
  /**
   * Where a raid starts: out along the road's far end past the top of the screen (grid offsets; the road's last point
   * (10.5, 12.8) is at the screen's top edge in the default shot, this ~25 px above it), in the haze.
   */
  static readonly Mist: readonly (readonly [number, number])[] = [[13.6, 14.4]];
  /** The path ends this far outside the camp gate's line (cells). */
  static readonly GateStop = 0.7;
  /** The models the raids may use: the core raiders and their occasional brute and flyer (LOD twins, loaded lazily). */
  static readonly Kinds: readonly RaiderKind[] = ["grunt", "runner", "shieldbearer", "brute", "flyer"];
  /** Seconds before trying again when the party would not fit under the cap or the art is not in yet. */
  static readonly Retry = 2;
  /** The bolt aims where its target will be when it lands (a strike's leader, s). */
  static readonly Leader = 0.1;
  /** Blob shadow size per world unit of the model's width, and its opacity. */
  static readonly Blob = { k: 3.2, alpha: 0.3, lift: 0.012 } as const;
  /** The raiders drawn larger than on a map, as the camp's troops are (HubCamp.TroopScale): they read at the hub's distance. */
  static readonly Scale = 1.6;
  /** A bolt lands this high on a walker (world units). */
  static readonly HitLift = 0.15;

  readonly group = new THREE.Group();
  private readonly path: RaidPath;
  private readonly pool = new Map<RaiderKind, Raider[]>();
  private readonly raiders: Raider[] = [];
  private plan: RaidPlan | null = null;
  private crew: (Raider | null)[] = [];
  private bolt = 0;
  private clock = 0;
  private wait = nextRaidIn(Math.random, true);
  private time = 0;
  private ready = false;
  private loading = false;
  private worn: VFX | null = null;
  private readonly shadows: THREE.InstancedMesh;
  private readonly shadowGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly m4 = new THREE.Matrix4();
  private readonly at = { x: 0, z: 0, dx: 0, dz: 0 };
  private readonly tip = new THREE.Vector3();
  private readonly aim = new THREE.Vector3();

  /**
   * `fortress` / `fortressName`: the fortress's model and its name (`base_t2__dragonlord`), for its conductor node;
   * `fx`: the hub's sprite VFX (null until loaded); `tier`: the quality tier index (0 ultra … 4 lowest).
   */
  constructor(private readonly balance: BalanceData, base: Cell, private readonly groundY: number, private readonly fortress: THREE.Object3D,
    private readonly fortressName: string, private readonly fx: () => VFX | null, private readonly tier: () => number) {
    const road = HubCampLayout.Road.filter(([, dz]) => dz > HubCampLayout.NorthWall);
    const pts = [...HubRaid.Mist, ...[...road].reverse()].map(([dx, dz]) => [base.x + dx, base.y + dz] as const);
    pts.push([base.x + HubCampLayout.Gate[0], base.y + HubCampLayout.Gate[1] + HubRaid.GateStop]);
    this.path = new RaidPath(pts);
    this.shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: HubRaid.Blob.alpha, depthWrite: false, toneMapped: false });
    this.shadows = new THREE.InstancedMesh(this.shadowGeo, this.shadowMat, HUB_RAID.cap);
    this.shadows.count = 0;
    this.shadows.renderOrder = 1;
    this.shadows.frustumCulled = false;
    this.group.add(this.shadows);
  }

  /** The LOD model names the raids draw (loaded after the hub is up, so the hub opens no slower). */
  static assetNames(): string[] { return HubRaid.Kinds.map((k) => `${hqEnemyName(k)}_lod1`); }

  update(dt: number): void {
    this.time += dt;
    const reduced = reducedVfx(), vfx = this.fx();
    if (vfx) { vfx.takeTrauma(); if (this.worn !== vfx) { this.worn = vfx; SkinFx.wearStrike(vfx, this.fortressName); } }
    if (this.plan) this.run(dt, vfx, reduced);
    else if ((this.wait -= dt) <= 0) this.start(reduced, vfx);
    this.step(dt, reduced, vfx);
  }

  /** Begins the next raid when it can: effects on, the VFX and the art loaded, room under the cap. */
  private start(reduced: boolean, vfx: VFX | null): void {
    this.wait = HubRaid.Retry;
    if (reduced || !vfx || !this.loaded()) return;
    const cap = this.tier() >= 3 ? HUB_RAID.capLow : HUB_RAID.cap;
    const free = cap - this.raiders.filter((r) => r.plan || r.dying).length;
    if (free < 2) return;
    vfx.setQuality(this.tier());
    const plan = planRaid(Math.random, this.path.length, (k) => this.speedOf(k), free);
    this.crew = plan.raiders.map((p) => this.acquire(p.kind, p));
    this.plan = plan;
    this.clock = 0;
    this.bolt = 0;
  }

  /** The raid's clock: raiders set off on their delays, bolts fire on theirs. */
  private run(dt: number, vfx: VFX | null, reduced: boolean): void {
    const plan = this.plan!;
    this.clock += dt;
    for (; this.bolt < plan.bolts.length && plan.bolts[this.bolt].time <= this.clock; this.bolt++) this.fire(plan.bolts[this.bolt], vfx, reduced);
    if (this.bolt >= plan.bolts.length && this.crew.every((r) => !r || r.dead)) { this.plan = null; this.crew = []; this.wait = nextRaidIn(Math.random); }
  }

  private fire(b: BoltPlan, vfx: VFX | null, reduced: boolean): void {
    const r = this.crew[b.raider];
    if (!r || r.dead) return;
    const v = r.v, p = r.plan!;
    this.path.sample(r.s + (b.strike ? p.speed * HubRaid.Leader : 0), p.lane, this.at);
    this.aim.set(this.at.x, this.groundY + (v.air ? v.body.position.y + v.height * v.scale * 0.5 : HubRaid.HitLift), this.at.z);
    const tip = this.conductor();
    if (b.lethal) r.dead = true;                                     // it runs on until the bolt lands, then falls
    if (!vfx) { this.hit(r, b.lethal, reduced, null); return; }
    if (!b.strike) { vfx.baseZap(tip, this.aim); this.hit(r, b.lethal, reduced, vfx); return; }
    vfx.baseStrike(tip, this.aim, { onStrike: (to) => {
      const L = StrikeBeats.light;
      if (!reduced) StrikeLight.fire(to.clone().setY(to.y + L.lift), L.colour, L.intensity, L.range, L.seconds);
      this.hit(r, b.lethal, reduced, vfx);
    } });
  }

  /** A bolt lands on `r`: a stagger, or its death (the level's: the topple or the flyer's spiral, then the dissolve). */
  private hit(r: Raider, lethal: boolean, reduced: boolean, vfx: VFX | null): void {
    const v = r.v;
    hitFeel(v, this.tip.x, this.tip.z, reduced);
    if (!lethal) { v.rig.playOnce("hit", false, null, { solo: true }); return; }
    r.dying = true;
    v.bar.dying = true;
    v.dead = 0;
    v.knock.x = v.knock.v = 0;
    startDeath(v, reduced, null, vfx);
    KillFx.onKill(v, vfx, reduced);
    vfx?.unitDeath(this.aim.set(v.root.position.x, this.groundY + HubRaid.HitLift, v.root.position.z), { coins: 0 });
  }

  /** Walks the live raiders and plays the deaths; parks the finished ones; places the blobs. */
  private step(dt: number, reduced: boolean, vfx: VFX | null): void {
    let n = 0;
    for (const r of this.raiders) {
      if (!r.plan && !r.dying) continue;
      const v = r.v;
      if (r.dying) {
        KillFx.onDying(v, vfx, dt, reduced);
        if (dieStep(v, dt, reduced, this.time, NO_STAGE, noRing)) { this.park(r); continue; }
      } else {
        const p = r.plan!;
        if (this.clock < p.delay) { v.root.visible = false; continue; }
        v.root.visible = true;
        r.s = Math.min(this.path.length, r.s + p.speed * dt);
        this.path.sample(r.s, p.lane, this.at);
        v.cur.set(this.at.x, 0, this.at.z);
        // a sim tick's step behind, so the view reads its speed as the sim's (enemyFeel.ts stepUnit)
        v.prev.set(this.at.x - this.at.dx * p.speed / TICKS, 0, this.at.z - this.at.dz * p.speed / TICKS);
        stepUnit(v, 1, dt, this.time, NO_STAGE, reduced);
        v.root.position.y = this.groundY;
        v.look?.update(dt, this.time, reduced);
        this.emerge(v, r.s);
      }
      if (n < this.shadows.instanceMatrix.count) {
        const k = HubRaid.Blob.k * Math.max(0.12, v.ringWidth * 0.5) * (r.dying ? Math.max(0, 1 - v.dead) : 1);
        this.m4.makeScale(k, 1, k).setPosition(v.root.position.x, this.groundY + HubRaid.Blob.lift, v.root.position.z);
        this.shadows.setMatrixAt(n++, this.m4);
      }
    }
    this.shadows.count = n;
    if (n) this.shadows.instanceMatrix.needsUpdate = true;
  }

  /** Out of the mist: the unit's own dissolve run backwards with no edge glow, then its stock edge back once solid. */
  private emerge(v: EnemyView, s: number): void {
    const look = v.look;
    if (!look || look.dissolve === 0) return;
    look.dissolve = mistFade(s);
    if (look.dissolve === 0) look.tintEdge(null);
  }

  /** The conductor's tip, world space (the fortress's top when a skin has none). */
  private conductor(): THREE.Vector3 {
    const node = hqNode(this.fortress, this.fortressName, "condgem0");
    if (node) return node.getWorldPosition(this.tip);
    const box = new THREE.Box3().setFromObject(this.fortress);
    return box.getCenter(this.tip).setY(box.max.y);
  }

  private speedOf(k: RaiderKind): number {
    const a = this.arch(k);
    const s = a ? a.speedCellsPerSecond.toNumber() : 0;
    return s > 0 ? s : HUB_RAID.fallbackSpeed;
  }

  private arch(k: RaiderKind): EnemyArchetype | undefined { return this.balance.enemies.find((e) => e.id === k); }

  /** Whether the raiders' art is in; the first ask starts the load. */
  private loaded(): boolean {
    const L = lib();
    if (!L) return false;
    if (this.ready) return true;
    const names = HubRaid.assetNames().filter((n) => L.ships(n));
    if (names.length && names.every((n) => L.hq(n))) return (this.ready = true);
    if (!this.loading && names.length) { this.loading = true; void L.ensure(names).finally(() => { this.loading = false; }); }
    return false;
  }

  /** A pooled view of `kind` readied for a new run (built once per pooled slot). */
  private acquire(kind: RaiderKind, plan: RaidPlan["raiders"][number]): Raider | null {
    const L = lib(), arch = this.arch(kind), name = `${hqEnemyName(kind)}_lod1`;
    if (!L || !arch || !L.hq(name)) return null;
    let r = this.pool.get(kind)?.pop();
    const role = enemyLook(arch);
    const e = standIn(arch.isAir);
    if (!r) {
      const v = buildEnemyView(e, kind, `hq:${name}`, role, kind, (m) => L.height(m) || 0.45, NO_STAGE);
      v.body.traverse((o) => { o.castShadow = false; });          // a soft blob below instead (the hub's shadow map is static)
      const k = HubRaid.Scale;                                       // its stride grows with it, so its feet stay planted
      v.scale *= k; v.stride *= k; v.headY *= k; v.headRest *= k; v.ringWidth *= k;
      this.group.add(v.root);
      r = { v, plan: null, s: 0, dead: false, dying: false };
      this.raiders.push(r);
    }
    // no role wash: the hub is a scene, not play (as the title's diorama: enemyFeel.ts EnemyWash)
    const wash = EnemyWash.on;
    EnemyWash.on = false;
    resetEnemyView(r.v, e, arch, role);
    EnemyWash.on = wash;
    r.v.age = EMERGE_SECONDS;                                       // out of the haze, not out of the ground
    if (r.v.look) { r.v.look.tintEdge(MIST_EDGE); r.v.look.dissolve = 1; }   // hidden until it walks out of the mist
    r.v.root.visible = false;
    r.plan = plan; r.s = 0; r.dead = false; r.dying = false;
    return r;
  }

  private park(r: Raider): void {
    r.plan = null; r.dying = false; r.dead = true;
    r.v.root.visible = false;
    const kind = r.v.archId as RaiderKind;
    if (!this.pool.has(kind)) this.pool.set(kind, []);
    this.pool.get(kind)!.push(r);
  }

  dispose(): void {
    for (const r of this.raiders) RigidRig.dispose(r.v.root);
    this.raiders.length = 0;
    this.pool.clear();
    this.shadowGeo.dispose();
    this.shadowMat.map?.dispose();
    this.shadowMat.dispose();
    this.group.removeFromParent();
  }
}

/** The dissolve edge while a raider walks out of the mist: black adds no glow, so it simply thickens out of the haze. */
const MIST_EDGE = 0x000000;
/** Sim ticks a second (the views read speed per tick). */
const TICKS = 20;
/** The hub's raiders are never bosses, so the boss stage the level's views take is never touched. */
const NO_STAGE = null as unknown as BossStage;
const noRing = (): void => {};
/** What the enemy views read off a sim enemy, for a hub raider that has none: not a boss, full health. */
const FULL = { toNumber: () => 1 };
function standIn(air: boolean): Enemy { return { isBoss: false, isAir: air, hp: FULL, maxHp: FULL, archetype: 0 } as unknown as Enemy; }
